import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useDisclosure,
  HStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';

const API_BASE = '/api';

function Payments() {
  const toast = useToast();
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ 
    customer_id: '', amount: '', currency: 'gbp', status: 'created', description: '' 
  });
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [paymentToDelete, setPaymentToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchData = () => {
    Promise.all([
      fetch(`${API_BASE}/payments/`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_BASE}/customers/`, { headers: getAuthHeaders() }).then(r => r.json()),
    ]).then(([pays, custs]) => {
      setPayments(Array.isArray(pays) ? pays : []);
      setCustomers(Array.isArray(custs) ? custs : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAddPayment = () => {
    setEditingPayment(null);
    setPaymentForm({ customer_id: '', amount: '', currency: 'gbp', status: 'created', description: '' });
    onModalOpen();
  };

  const openEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({ 
      customer_id: payment.customer_id, 
      amount: payment.amount,
      currency: payment.currency,
      status: payment.status,
      description: payment.description || ''
    });
    onModalOpen();
  };

  const handleSavePayment = async () => {
    const method = editingPayment ? 'PUT' : 'POST';
    const url = editingPayment 
      ? `${API_BASE}/payments/${editingPayment.id}/` 
      : `${API_BASE}/payments/`;
    
    const payload = { ...paymentForm };
    if (!editingPayment) {
      payload.amount = parseInt(paymentForm.amount) || 0;
    }
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        fetchData();
        toast({ title: editingPayment ? 'Payment updated' : 'Payment created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving payment', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving payment', status: 'error', duration: 3000 });
    }
  };

  const confirmDelete = (payment) => {
    setPaymentToDelete(payment);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!paymentToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/payments/${paymentToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchData();
        toast({ title: 'Payment deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting payment', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting payment', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setPaymentToDelete(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return 'green';
      case 'failed': return 'red';
      case 'canceled': return 'gray';
      case 'refunded': return 'orange';
      default: return 'blue';
    }
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Payments</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={openAddPayment}>
          Add Payment
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Customer</Th>
              <Th>Stripe ID</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th>Description</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {payments.map(payment => (
              <Tr key={payment.id}>
                <Td fontWeight="medium">{payment.customer_email}</Td>
                <Td><Text fontSize="sm" fontFamily="mono">{payment.stripe_id}</Text></Td>
                <Td>{formatAmount(payment.amount, payment.currency)}</Td>
                <Td>
                  <Badge colorScheme={getStatusColor(payment.status)}>
                    {payment.status}
                  </Badge>
                </Td>
                <Td>{payment.description || '-'}</Td>
                <Td>{new Date(payment.created_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditPayment(payment)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(payment)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {payments.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No payments found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingPayment ? 'Edit Payment' : 'Add Payment'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!editingPayment && (
              <>
                <FormControl mb={4}>
                  <FormLabel>Customer</FormLabel>
                  <Select
                    value={paymentForm.customer_id}
                    onChange={(e) => setPaymentForm({ ...paymentForm, customer_id: e.target.value })}
                    placeholder="Select customer"
                  >
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.email}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Amount (in cents)</FormLabel>
                  <Input
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    placeholder="1000"
                  />
                </FormControl>
                <FormControl mb={4}>
                  <FormLabel>Currency</FormLabel>
                  <Select
                    value={paymentForm.currency}
                    onChange={(e) => setPaymentForm({ ...paymentForm, currency: e.target.value })}
                  >
                    <option value="gbp">GBP</option>
                    <option value="usd">USD</option>
                    <option value="eur">EUR</option>
                  </Select>
                </FormControl>
              </>
            )}
            <FormControl mb={4}>
              <FormLabel>Status</FormLabel>
              <Select
                value={paymentForm.status}
                onChange={(e) => setPaymentForm({ ...paymentForm, status: e.target.value })}
              >
                <option value="created">Created</option>
                <option value="succeeded">Succeeded</option>
                <option value="failed">Failed</option>
                <option value="canceled">Canceled</option>
                <option value="refunded">Refunded</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={paymentForm.description}
                onChange={(e) => setPaymentForm({ ...paymentForm, description: e.target.value })}
                placeholder="Payment description"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleSavePayment}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Payment</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this payment? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Payments;
